import React from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { ArrowLeftIcon, BadgeCheckIcon, ClockIcon, MapPinIcon, MessageSquareIcon, StarIcon } from 'lucide-react';
import { Badge } from '../../../components/ui/Badge';
import { Button } from '../../../components/ui/Button';
import { Panel } from '../../../components/ui/Panel';
import { EmptyState } from '../../../components/ui/EmptyState';
import { ReportControls } from '../../../components/trust/ReportControls';
import { ItemCard } from '../../../components/market/ItemCard';
import { marketCategories, marketItems, marketSellers } from '../../../data/market';

/**
 * A seller's own page. Whether the seller is a registered vendor or another
 * student, the same facts are shown in the same order — who they are, what
 * Campuzon has actually checked, and everything they currently offer.
 */
export function ProviderProfile() {
  const {
    providerId
  } = useParams<{
    providerId: string;
  }>();
  const seller = marketSellers.find(entry => entry.id === providerId);
  if (!seller) return <Navigate to="/market" replace data-id="element-9956" data-mp="3q06e0r6h104e" />;
  const items = marketItems.filter(item => item.sellerId === seller.id);
  const categories = Array.from(new Set(items.map(item => item.category)));
  return <div className="space-y-8 pb-8" data-id="element-9957" data-mp="3orcshcxvr0q">
      <div data-id="element-9958" data-mp="3v0u151rkwwtc">
        <Link to="/market" className="inline-flex min-h-touch items-center gap-1.5 text-body-sm font-medium text-ink-muted hover:text-ink" data-id="element-9959" data-mp="3v13xd737w07h">
          <ArrowLeftIcon aria-hidden className="h-4 w-4" data-id="element-9960" data-mp="8lrfciakybn3" />
          Marketplace
        </Link>
      </div>

      <header className="border-b border-line pb-7" data-id="element-9961" data-mp="mlacyh5rm4l5">
        <p className="text-body-sm text-ink-muted" data-id="element-9962" data-mp="mkr5xvhnl0lw">{seller.kind}</p>
        <h1 className="mt-1 text-h1 font-semibold tracking-tight text-ink" data-id="element-9963" data-mp="hnimzsooksjl">
          {seller.displayName}
        </h1>
        <p className="cz-num mt-1 text-body text-ink-muted" data-id="element-9964" data-mp="2odfcoetia66f">{seller.handle}</p>

        <div className="mt-4 flex flex-wrap items-center gap-2.5" data-id="element-9965" data-mp="2jevr4fitqwel">
          {seller.verified ? <Badge tone="ok" data-id="element-9966" data-mp="nbpkmyn8f47f">
              <BadgeCheckIcon aria-hidden className="h-4 w-4" data-id="element-9967" data-mp="nciitb84kldy" />
              Verified provider
            </Badge> : <Badge tone="warn" data-id="element-9968" data-mp="x8vfmhyyvfcx">Not inspected by Campuzon</Badge>}
          {seller.rating !== undefined && <span className="cz-num inline-flex items-center gap-1.5 text-body-sm text-ink-muted" data-id="element-9969" data-mp="11apauaqe7r5p">
              <StarIcon aria-hidden className="h-4 w-4 text-warn-700" data-id="element-9970" data-mp="16abogla4v9f1" />
              {seller.rating.toFixed(1)} from {seller.reviewCount} completed orders
            </span>}
        </div>

        <ul className="mt-4 flex flex-wrap gap-x-6 gap-y-2 text-body-sm text-ink-muted" data-id="element-9971" data-mp="1fz39sakouvgz">
          <li className="flex items-center gap-1.5" data-id="element-9972" data-mp="1kyy1uhg79r3m">
            <MapPinIcon aria-hidden className="h-4 w-4" data-id="element-9973" data-mp="1kxn16qi1wzgq" />
            {seller.campus}
          </li>
          {seller.hoursLabel && <li className="flex items-center gap-1.5" data-id="element-9974" data-mp="1tyuusv9mhc4h">
              <ClockIcon aria-hidden className="h-4 w-4" data-id="element-9975" data-mp="1tze3dldduo5u" />
              {seller.hoursLabel}
            </li>}
          {seller.responseRateLabel && <li className="flex items-center gap-1.5" data-id="element-9976" data-mp="39hsqc0wf9r2j">
              <MessageSquareIcon aria-hidden className="h-4 w-4" data-id="element-9977" data-mp="39kezu62vigne" />
              {seller.responseRateLabel}
            </li>}
        </ul>

        <div className="mt-5 flex flex-wrap items-center gap-3" data-id="element-9978" data-mp="30ifi1bk1b8je">
          <Button variant="primary" icon={<MessageSquareIcon className="h-4 w-4" data-id="element-9980" data-mp="117emcceojvq" />} data-id="element-9979" data-mp="128zt9n4ymr6">
            Message {seller.displayName}
          </Button>
          <ReportControls subject={seller.displayName} surface="profile" allowBlock data-id="element-9981" data-mp="3t7cmaarev4t5" />
        </div>
      </header>

      <Panel title="What verification here actually means" data-id="element-9982" data-mp="3odi6vjiu9bzv">
        <p className="max-w-[66ch] text-body-sm text-ink" data-id="element-9983" data-mp="3jh1jirjgr1nu">
          {seller.verified ? 'Campuzon has confirmed who this seller is and that they are permitted to trade on this campus. It is not an inspection of any individual item, not an endorsement of quality, and not a guarantee that a refund will be available.' : 'Campuzon has not checked this seller. They may be entirely reliable, but nothing about them has been independently confirmed. Agree terms in writing, meet in a public place on campus, and keep payment inside Campuzon so there is a record.'}
        </p>
      </Panel>

      <section aria-labelledby="provider-listings" data-id="element-9984" data-mp="3rougp0mrw45c">
        <div className="flex flex-wrap items-baseline justify-between gap-3" data-id="element-9985" data-mp="3rpnqkah139rp">
          <h2 id="provider-listings" className="text-h3 font-semibold text-ink" data-id="element-9986" data-mp="j8sx1lm4f1qa">
            Everything from this seller
          </h2>
          <p className="text-caption text-ink-muted" data-id="element-9987" data-mp="9ig3x508q08j">
            {items.length} listing{items.length === 1 ? '' : 's'} across{' '}
            {categories.length} categor{categories.length === 1 ? 'y' : 'ies'}
          </p>
        </div>

        {items.length === 0 ? <div className="mt-4" data-id="element-9988" data-mp="1250wu9wd6ke7">
            <EmptyState title="Nothing listed right now" description="This seller has no active listings. Their record and reviews stay here so you can still see who they are." data-id="element-9989" data-mp="16y3uat49idzo" />
          </div> : <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-id="element-9990" data-mp="1cak2t8zcdzwv">
            {items.map(item => {
          const category = marketCategories.find(entry => entry.id === item.category);
          return <ItemCard key={item.id} item={item} pattern={category?.pattern ?? 'goods'} data-id="element-9991" data-mp="xdjllzes1ncn" />;
        })}
          </div>}
      </section>
    </div>;
}